export type CookieYesConsent = Record<string, string>;

export type ConsentMethod =
  | 'accept_all'
  | 'reject_all'
  | 'custom_preferences'
  | 'settings_update';

export const parseCookieYesConsent = (): CookieYesConsent => {
  if (typeof document === 'undefined') return {};
  const raw = document.cookie
    .split('; ')
    .find((c) => c.startsWith('cookieyes-consent='))
    ?.split('=')[1];
  if (!raw) return {};
  const decoded = decodeURIComponent(raw);
  return Object.fromEntries(
    decoded
      .split(/[&,]/)
      .map((entry) => entry.split(/[:=]/))
      .filter(([key, value]) => key && value != null)
  );
};

export const isCookieYesCategoryAllowed = (
  consent: CookieYesConsent,
  category: string
): boolean => consent[category] === 'yes';

export const isAnalyticsConsentGranted = (
  consent: CookieYesConsent = parseCookieYesConsent()
): boolean => isCookieYesCategoryAllowed(consent, 'analytics');

export const hasCookieYesConsentDecision = (
  consent: CookieYesConsent = parseCookieYesConsent()
): boolean => {
  if (Object.keys(consent).length === 0) return false;
  return (
    consent.consent === 'yes' ||
    consent.consent === 'no' ||
    consent.action === 'yes' ||
    consent.action === 'reject' ||
    'analytics' in consent
  );
};

export const resolveConsentMethod = (
  consent: CookieYesConsent,
  hadPreviousDecision: boolean
): ConsentMethod => {
  if (hadPreviousDecision) return 'settings_update';

  const analytics = isCookieYesCategoryAllowed(consent, 'analytics');
  const functional = isCookieYesCategoryAllowed(consent, 'functional');
  const performance = isCookieYesCategoryAllowed(consent, 'performance');
  const advertisement = isCookieYesCategoryAllowed(consent, 'advertisement');

  const allAccepted = analytics && functional && performance && advertisement;
  const allRejected = !analytics && !functional && !performance && !advertisement;

  if (consent.consent === 'yes' || allAccepted) return 'accept_all';
  if (consent.consent === 'no' || consent.action === 'reject' || allRejected) {
    return 'reject_all';
  }

  return 'custom_preferences';
};

export const getConsentUpdatedProperties = (
  consent: CookieYesConsent,
  consentMethod: ConsentMethod
) => ({
  consent_status: isAnalyticsConsentGranted(consent) ? 'granted' : 'denied',
  consent_analytics: isAnalyticsConsentGranted(consent),
  consent_functional: isCookieYesCategoryAllowed(consent, 'functional'),
  consent_performance: isCookieYesCategoryAllowed(consent, 'performance'),
  consent_advertisement: isCookieYesCategoryAllowed(consent, 'advertisement'),
  consent_method: consentMethod,
});

const asStringArray = (value: unknown): string[] =>
  Array.isArray(value) ? value.map((entry) => String(entry).toLowerCase()) : [];

const cookieYesEventDetail = (
  event?: Event
): Record<string, unknown> | null => {
  if (!event || !('detail' in event)) return null;
  const detail = (event as CustomEvent<Record<string, unknown>>).detail;
  if (!detail || typeof detail !== 'object') return null;
  return detail;
};

const categoryFromCookieYesDetail = (
  detail: Record<string, unknown>,
  category: string
): 'yes' | 'no' | undefined => {
  const accepted = asStringArray(detail.accepted);
  const rejected = asStringArray(detail.rejected);
  if (accepted.includes(category)) return 'yes';
  if (rejected.includes(category)) return 'no';

  const categories =
    detail.categories && typeof detail.categories === 'object'
      ? (detail.categories as Record<string, unknown>)
      : detail;

  const value = categories[category];
  if (value === 'yes' || value === true) return 'yes';
  if (value === 'no' || value === false) return 'no';
  return undefined;
};

export const resolveAnalyticsFromCookieYesEvent = (
  event?: Event
): boolean | undefined => {
  const detail = cookieYesEventDetail(event);
  if (!detail) return undefined;
  const analytics = categoryFromCookieYesDetail(detail, 'analytics');
  if (analytics === 'yes') return true;
  if (analytics === 'no') return false;
  return undefined;
};

export const resolveCookieYesConsentFromEvent = (
  event?: Event
): CookieYesConsent | null => {
  const parsed = parseCookieYesConsent();
  if (hasCookieYesConsentDecision(parsed)) return parsed;

  const detail = cookieYesEventDetail(event);
  if (!detail) return null;

  const analytics = categoryFromCookieYesDetail(detail, 'analytics');
  if (!analytics) return null;

  return {
    consent: analytics === 'yes' ? 'yes' : 'no',
    analytics,
    functional: categoryFromCookieYesDetail(detail, 'functional') ?? 'no',
    performance: categoryFromCookieYesDetail(detail, 'performance') ?? 'no',
    advertisement: categoryFromCookieYesDetail(detail, 'advertisement') ?? 'no',
  };
};

const OPTIONAL_CATEGORIES = [
  'analytics',
  'functional',
  'performance',
  'advertisement',
] as const;

export type CookieYesConsentUpdateDetail = {
  accepted?: string[];
  rejected?: string[];
};

const toCategorySet = (values?: string[]): Set<string> =>
  new Set((values ?? []).map((value) => value.trim().toLowerCase()).filter(Boolean));

export const resolveConsentMethodFromBanner = ({
  consent,
  hadPreviousDecision,
  detail,
  bannerAction,
}: {
  consent: CookieYesConsent;
  hadPreviousDecision: boolean;
  detail?: CookieYesConsentUpdateDetail | null;
  bannerAction?: 'accept_all' | 'reject_all' | 'custom_preferences' | null;
}): ConsentMethod => {
  if (hadPreviousDecision) return 'settings_update';
  if (bannerAction) return bannerAction;

  const accepted = toCategorySet(detail?.accepted);
  const rejected = toCategorySet(detail?.rejected);

  if (accepted.size > 0 || rejected.size > 0) {
    const allOptionalAccepted = OPTIONAL_CATEGORIES.every((category) =>
      accepted.has(category)
    );
    const allOptionalRejected = OPTIONAL_CATEGORIES.every(
      (category) => rejected.has(category) || !accepted.has(category)
    );

    if (allOptionalAccepted) return 'accept_all';
    if (allOptionalRejected && !accepted.has('analytics')) return 'reject_all';
    return 'custom_preferences';
  }

  return resolveConsentMethod(consent, false);
};
